/**
 * La sesión del operador. **Aislada a propósito.**
 *
 * Todo el acceso al token pasa por este archivo: ningún componente ni query lo toca.
 * Eso es lo que hizo que cambiar de `ADMIN_TOKEN` compartido a sesiones de persona
 * fuera reescribir esto y nada más.
 *
 * ## Dos decisiones que acotan el riesgo
 *
 * - **`sessionStorage`, nunca `localStorage`.** Muere al cerrar la pestaña. Un token de
 *   administración que sobrevive semanas en el disco de un portátil es una credencial
 *   olvidada esperando a filtrarse.
 * - **Se guarda también cuándo caduca**, para poder tratar una sesión vencida como lo
 *   que es —hay que volver a entrar— en vez de esperar a que la primera petición falle
 *   con un 401 delante del operador.
 *
 * `sessionStorage` sigue estando expuesto a XSS, y eso no se arregla desde aquí: la
 * alternativa real es servir la consola bajo el mismo dominio que el gateway y usar
 * cookies `httpOnly`, que además haría desaparecer el CORS entero. Está anotado en el
 * plan; mientras tanto, esto es estrictamente mejor que el secreto compartido y eterno
 * que había antes.
 *
 * ## Lo que NO se guarda aquí
 *
 * El rol y los permisos **no**. Vienen de `/admin/auth/me` y viven en TanStack Query,
 * como cualquier otro dato del servidor. Copiarlos aquí crearía una segunda fuente que
 * se queda vieja justo cuando importa: cuando a alguien le acaban de cambiar el rol.
 */

const CLAVE = "alpaca-sesion"

type Sesion = {
  token: string
  /** Marca de tiempo en milisegundos, no segundos: es lo que devuelve `Date.now()`. */
  expiraEn: number
}

/** Se dispara cuando la sesión deja de valer, desde donde sea. */
export const EVENTO_SESION = "alpaca:sesion"

function leer(): Sesion | null {
  const crudo = sessionStorage.getItem(CLAVE)
  if (crudo === null) return null

  try {
    const s = JSON.parse(crudo) as Partial<Sesion>
    if (typeof s.token !== "string" || typeof s.expiraEn !== "number") return null
    return { token: s.token, expiraEn: s.expiraEn }
  } catch {
    // Entrada corrupta: se trata como no haber sesión. El siguiente login la
    // sobrescribe y nadie se entera.
    return null
  }
}

export function getToken(): string | null {
  const sesion = leer()
  if (sesion === null) return null

  if (sesion.expiraEn <= Date.now()) {
    // Un token caducado no se manda: el gateway lo rechazaría igual, pero mandarlo
    // gasta una ida y vuelta para llegar a la conclusión que ya se tenía aquí.
    clearSesion()
    return null
  }
  return sesion.token
}

export function setSesion(token: string, duracionSegundos: number): void {
  const sesion: Sesion = { token, expiraEn: Date.now() + duracionSegundos * 1000 }
  sessionStorage.setItem(CLAVE, JSON.stringify(sesion))
  window.dispatchEvent(new Event(EVENTO_SESION))
}

export function clearSesion(): void {
  sessionStorage.removeItem(CLAVE)
  window.dispatchEvent(new Event(EVENTO_SESION))
}

export function isAuthenticated(): boolean {
  return getToken() !== null
}
